<template>
    <div>
        <div class="p-10 text-center" v-if="isLoading">{{ getStaticTranslation('loading') }}</div>
        <div class="card" v-if="project && version">
            <DataTable
                ref="roleTable"
                v-model:filters="filters"
                :value="filteredTasks"
                paginator
                :rows="rows"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                tableClass="roleTable"
                headerClass="text-xs font-medium"
                dataKey="id"
                editMode="cell"
                :scrollHeight="tableHeight + 'px'"
                v-model:selection="selectedTasks"
                @rowReorder="onRowReorder"
                @page="onPageChange"
                @cell-edit-init="onCellEditInit"
                @cell-edit-complete="onCellEditComplete"
                @cell-edit-cancel="onCellEditCancel"
                filterDisplay="row"
                :globalFilterFields="selectedSection ? ['sections.' + project.sections.findIndex(section => section.id == selectedSection) + '.pivot.value'] : project.sections.filter(section => section.show_in_table).map((section, index) => 'sections.' + index + '.pivot.value')"
                :pt="{ table: { style: 'height: 1px; width: auto' }, header: { class: 'sticky top-0 z-20 bg-gray-200 px-4 py-2' }, footer: {class:'bg-primary-900 p-2'}}"
                v-if="project.sections.length && project.roles.length"
            >
            <template #header>
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <InputGroup class="w-full lg:w-24rem" style="max-width: 400px;">
                            <InputText v-model="filters['global'].value" type="search" :placeholder="getStaticTranslation('search')" />
                            <InputGroupAddon>
                                <i class="pi pi-search" />
                            </InputGroupAddon>
                        </InputGroup>
                        <Select
                            v-model="selectedSection"
                            :options="sectionOptions"
                            optionLabel="name"
                            optionValue="id"
                            :placeholder="getStaticTranslation('all_sections')"
                            showClear
                            @change="onSectionChange"
                            class="w-48 ml-3"
                        />
                        <div class="border-r border-gray-300 mr-3 ml-3 w-5 h-8" v-if="hiddenColumnsCount > 0"></div>
                        <Button
                            icon="pi pi-eye-slash"
                            outlined
                            @click="showHiddenColumnsMenu"
                            :v-tooltip="getStaticTranslation('show_hidden_columns')"
                            variant="outlined"
                            severity="secondary"
                            v-if="hiddenColumnsCount > 0"
                        />
                    </div>
                    <Button
                        :icon="hideEmptyValues ? 'pi pi-eye' : 'pi pi-eye-slash'"
                        :label="hideEmptyValues ? getStaticTranslation('show_empty_values') : getStaticTranslation('hide_empty_values')"
                        severity="secondary"
                        outlined
                        @click="toggleHideEmptyValues"
                    />
                </div>
                <Popover ref="hiddenColumnsPopover" :pt="{root: {class: 'bg-white border border-gray-300 rounded-lg shadow-lg'}}" v-if="hiddenColumnsCount > 0">
                    <div class="p-4 min-w-48">
                        <h6 class="font-medium mb-3">{{ getStaticTranslation('hidden_columns') }} ({{ hiddenColumnsCount }})</h6>
                        <div v-if="hiddenColumnsCount === 0" class="text-gray-500 text-sm">
                            {{ getStaticTranslation('no_hidden_columns') }}
                        </div>
                        <div v-else>
                            <div v-for="section in hiddenSections" :key="section.id" class="flex items-center justify-between mb-2">
                                <span class="text-sm mr-2">{{ getDisplaySectionName(section) }}</span>
                                <Button
                                    icon="pi pi-eye"
                                    outlined
                                    size="small"
                                    @click="toggleColumnVisibility(section.id)"
                                    :v-tooltip="getStaticTranslation('show_column')"
                                    :pt="{root: {class: 'border px-1 py-0.5 border-green-400 text-green-600 rounded text-xs'}}"
                                />
                            </div>
                        </div>
                    </div>
                </Popover>
            </template>
                <ColumnGroup type="header">
                    <Row>
                        <Column frozen :colspan="2" :rowspan="2" headerClass="!bg-gray-100" v-if="isProjectAdmin"></Column>
                        <Column :rowspan="2" v-for="(section, sectionIndex) in project.sections" :key="section.id" frozen headerClass="!bg-gray-100 align-bottom text-sm" :pt="{columnHeaderContent: {style: 'max-with: 300px; width: ' + (section.width ?? 'auto') + 'px'}}" :style="section.show_in_table ? '' : 'display: none'">
                            <template #header>
                                <div class="flex items-center justify-between gap-2 w-full">
                                    <div>{{ getDisplaySectionName(section) }}</div>
                                    <Button
                                        :icon="hasActiveColumnFilter(sectionIndex) ? 'pi pi-filter-fill' : 'pi pi-filter'"
                                        :severity="hasActiveColumnFilter(sectionIndex) ? 'primary' : 'secondary'"
                                        outlined :rowspan="2"
                                        link
                                        size="small"
                                        class="w-auto !p-0"
                                        @click="toggleColumnFilter($event, sectionIndex)"
                                    />
                                    <Popover :ref="`filterPopover_${sectionIndex}`" :pt="{root: {class: 'bg-white border border-gray-300 rounded-lg shadow-lg'}}">
                                        <div class="p-4 min-w-64" v-if="columnFilters['section_' + sectionIndex]">
                                            <h6 class="font-medium">{{ getStaticTranslation('filter') }}: {{ getDisplaySectionName(section) }}</h6>
                                            <Button icon="pi pi-eye-slash" :label="getStaticTranslation('hide_section')" class="my-2" outlined link size="small" severity="secondary" @click="toggleColumnVisibility(section.id)" />
                                            <div class="mb-3">
                                                <label class="text-sm font-medium mb-2 block">{{ getStaticTranslation('select_values') }}</label>
                                                <div class="max-h-64 overflow-y-auto border border-gray-200 rounded p-2">
                                                    <div v-for="item in columnFilters['section_' + sectionIndex].availableValues" :key="item.value" class="flex items-center mb-2" :class="{ 'opacity-40': item.disabled }">
                                                        <Checkbox
                                                            v-model="columnFilters['section_' + sectionIndex].selectedValues"
                                                            :value="item.value"
                                                            :inputId="`value_${sectionIndex}_${item.value}`"
                                                            :disabled="item.disabled"
                                                        />
                                                        <label :for="`value_${sectionIndex}_${item.value}`" class="ml-2 text-sm" :class="{ 'cursor-not-allowed': item.disabled, 'cursor-pointer': !item.disabled }">
                                                            {{ item.value }}
                                                        </label>
                                                    </div>
                                                    <div v-if="columnFilters['section_' + sectionIndex].availableValues.length === 0" class="text-gray-500 text-sm text-center py-2">
                                                        {{ getStaticTranslation('no_values_available') }}
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="flex justify-between gap-2">
                                                <Button
                                                    :label="getStaticTranslation('clear')"
                                                    icon="pi pi-times"
                                                    severity="secondary"
                                                    outlined
                                                    size="small"
                                                    @click="clearColumnFilter(sectionIndex)"
                                                    class="flex-1"
                                                />
                                                <Button
                                                    :label="getStaticTranslation('apply')"
                                                    icon="pi pi-check"
                                                    severity="primary"
                                                    size="small"
                                                    @click="applyColumnFilter(sectionIndex)"
                                                    class="flex-1"
                                                />
                                            </div>
                                        </div>
                                    </Popover>
                                </div>
                            </template>
                        </Column>
                        <template v-for="role in visibleRoles">
                            <Column :key="role.id" :header="getDisplayName(role)" :colspan="role.children.length" v-if="role.children && role.children.length > 0" :headerClass="['text-sm border-r border-b-0', getTextColorForBackground(role.color)]" :style="'background-color: #' + role.color" />
                        </template>
                    </Row>
                    <Row>
                        <Column v-for="child in visibleRoles.map(role => role.children.map((child) => child)).flat()" :key="child.id" :headerClass="['bg-stone-50 border-r p-2 text-stone-900 text-xs text-center  hover:opacity-80', user ? 'cursor-pointer' : '']" :pt="{headerCell: {class: 'hover:text-primary-950', style: 'background-color: #' + (child.user ? visibleRoles.find((e) => e.id == child.parent_id).color : 'EEEEEE')},}">
                            <template #header>
                                <div class="flex flex-col items-center" @click="selectRole(child)">
                                    <div class="absolute" style="top:38px;">
                                        <Avatar icon="pi pi-hourglass" class="opacity-80 !w-7 !h-7" :v-tooltip="getStaticTranslation('awaiting_signature')" style="background-color: #ffffff; color: #2a1261" shape="square" v-if="child.agreements && child.agreements.filter(e => !e.signature).length && child.agreements.filter(e => e.active).length == 0" />
                                        <Avatar icon="pi pi-ban" class="opacity-80 !w-7 !h-7" :v-tooltip="getStaticTranslation('no_user_assigned')" style="background-color: #ffffff; color: #2a1261" shape="square" v-else-if="!child.agreements || child.agreements.length == 0 || child.agreements.filter(e => !e.active).length == child.agreements.length" />
                                        <Avatar icon="pi pi-send" class="opacity-80 !w-7 !h-7" :v-tooltip="getStaticTranslation('role_updated_new_agreement')" style="background-color: #ffffff; color: #2a1261" shape="square" v-else-if="updatedRoles.map(e => e.id).includes(child.id)" />
                                    </div>
                                    <div class="font-light headerRole" @mouseenter="displayRole($event, child)" @mouseleave="hidePopover()">
                                        {{ getDisplayName(child) }}
                                    </div>
                                </div>
                            </template>
                        </Column>
                    </Row>
                </ColumnGroup>
                <Column class="rowReorder" frozen :rowReorder="!filters.global.value" :reorderableColumn="false" bodyClass="text-xs !bg-gray-50" bodyStyle="width: 2rem" v-if="isProjectAdmin"></Column>
                <Column frozen bodyClass="!bg-gray-50" bodyStyle="width: 2rem" v-if="isProjectAdmin">
                    <template #body="slotProps">
                        <span class="pi pi-cog cursor-pointer" :id="'cod-' + slotProps.data.id" @click="toggleActions($event, getIndex(slotProps))" />
                    </template>
                </Column>
                <Column
                    :field="'sections.' + sectionIndex + '.pivot.value'"
                    :filterField="'sections.' + sectionIndex + '.pivot.value'"
                    bodyClass="text-xs !bg-gray-50"
                    frozen
                    :style="section.show_in_table ? 'width: 150px' : 'display: none'"
                    v-for="(section, sectionIndex) in project.sections" :key="section.id"
                >
                    <template #body="slotProps">
                        <div v-if="hasVisibleSectionValue(slotProps.data, sectionIndex, currentViewLanguage)">
                            {{ getDisplayTaskSectionValue(slotProps.data.sections[sectionIndex].pivot.value) }}
                        </div>
                        <div v-else-if="isProjectAdmin">
                            <!-- Every text cell of a new/empty row must render a child element,
                                 not just the first (Divisie) column, otherwise PrimeVue's cell
                                 editor never activates on click (PIPE-565 issue 4). -->
                            <i class="text-xs text-gray-400">{{ getStaticTranslation('to_complete') }}</i>
                        </div>
                    </template>
                    <template #editor="slotProps">
                        <InputGroup
                        :fluid="true"
                            class="p-0"
                            v-if="slotProps.data.sections[sectionIndex] && slotProps.data.sections[sectionIndex].pivot && slotProps.data.sections[sectionIndex].pivot.value && isProjectAdmin"
                        >
                            <InputText
                                v-model="slotProps.data.sections[sectionIndex].pivot.value[currentViewLanguage]"
                                type="search"
                                @blur="onSectionInputBlur(slotProps.data.sections[sectionIndex].pivot)"
                            />
                        </InputGroup>
                        <div class="px-2" v-else-if="slotProps.data.sections[sectionIndex] && slotProps.data.sections[sectionIndex].pivot && slotProps.data.sections[sectionIndex].pivot.value && slotProps.data.sections[sectionIndex].pivot.value[currentViewLanguage]">
                            {{ getDisplayTaskSectionValue(slotProps.data.sections[sectionIndex].pivot.value) }}
                        </div>
                    </template>
                </Column>
                <Column v-for="child in getRoles" :key="child.id" headerClass="text-xs" bodyClass="border-x" style="min-width: 50px" :pt="{bodyCell: {class: '!p-0 border-x'}, footer: {class:'bg-stone-700'}}">
                    <template #body="slotProps">
                        <div :class="['w-full h-full flex items-center justify-center', { 'cursor-pointer': isProjectAdmin }, { 'bg-primary-50': columnSelected && columnSelected.id == child.id }]" @click="toggleCodes($event, slotProps.index, getRole((this.page * this.rows) + slotProps.index, child.id))">
                            <div
                                :class="['flex w-8 justify-center p-2 rounded text-xs', getTextColorForBackground(getRoleRaciCodeValue(slotProps.index, child.id).color)]"
                                :style="'background-color: #' + getRoleRaciCodeValue(slotProps.index, child.id).color"
                                :title="getDisplayRaciCodeName(getRoleRaciCodeValue(slotProps.index, child.id))"
                                v-if="getRoleRaciCodeValue(slotProps.index, child.id)"
                            >
                                {{  getRoleRaciCodeValue(slotProps.index, child.id).code }}
                            </div>
                        </div>
                    </template>
                </Column>
                <template #footer>
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <Button icon="pi pi-plus" class="text-white ml-24" :label="getStaticTranslation('add_new_task')" outlined @click="addNew" :pt="{label: {class: 'font-medium'}, root: {class: 'border px-2 py-1 border-white rounded'}}" v-if="isProjectAdmin && version" :disabled="scrollDown" />
                        </div>
                        <div class="flex justify-center">
                            <Button icon="pi pi-save" :label="getStaticTranslation('save')" severity="success" :loading="isSaving" class="ml-3" outlined @click="saveProjectVersion(false, true)" :pt="{label: {class: 'font-medium'}, root: {class: 'flex items-center border px-3 py-1 border-green-400 text-green-400 rounded'}}" v-if="edited" />
                        </div>
                        <div class="flex justify-end items-center" v-if="version">
                            <Button icon="pi pi-send" class="text-white mr-5" :label="getStaticTranslation('send_agreements')" @click="sendAgreementDialog=true" :pt="{label: {class: 'font-medium'}, root: {class: 'border px-2 py-1 border-white rounded text-nowrap'}}" v-if="version && updatedRoles.length && user && isProjectAdmin" />
                            <Button icon="pi pi-tag" :class="['text-white mr-5', user===null ? 'border-0' : '']" :label="version.name + (version.name ? '.' : '') + version.number" @click="versionDialog=user!==null" :disabled="user===null || (!isProjectAdmin && !isAdmin)" :pt="{label: {class: 'font-medium'}, root: {class: user===null || (!isProjectAdmin && !isAdmin) ? 'border-0  text-nowrap' : 'border px-2 py-1 border-white rounded  text-nowrap'}}" v-if="version" />
                            <Button icon="pi pi-external-link" class="text-white mr-2" :label="getStaticTranslation('export_csv')" outlined @click="exportCSV" :pt="{label: {class: 'font-medium'}, root: {class: 'border px-2 py-1 border-white rounded text-nowrap'}}" />
                        </div>
                    </div>
                </template>
                <template #paginatorend>
                    <Select
                        v-model="pageSizeOption"
                        :options="pageSizeOptions"
                        optionLabel="label"
                        optionValue="value"
                        class="w-32 ml-3"
                        :aria-label="getStaticTranslation('rows_per_page')"
                        @update:modelValue="onPageSizeChange"
                    />
                </template>
            </DataTable>

            <div class="container flex justify-center align-items-center flex-wrap pt-8" v-else>
                <Message severity="warn" :closable="false" class="mw-64 h-32 text-center">
                    <div class="mb-5"><i class="pi pi-exclamation-triangle"></i> {{ getStaticTranslation('project_needs_sections_roles') }}</div>
                    <NuxtLink to="/setup">
                        <div class="rounded bg-primary inline-block text-white px-4 py-2">{{ getStaticTranslation('setup') }}</div>
                    </NuxtLink>
                </Message>
            </div>

            <Popover ref="op" >
                <Listbox v-model="selectedCodeId" scrollHeight="20rem" :options="raciCodes" optionLabel="code" optionValue="id" :pt="{root: {class: 'border-0'}, list: {style: 'padding: 4px 0'}, option: {style: 'justify-content: center'}}" @update:modelValue="setCode">
                        <template #option="slotProps">
                            <div v-if="slotProps.option.id > 0">
                                {{ slotProps.option.code }}
                            </div>
                            <div class="text-red-700" v-else>
                                <i class="pi pi-trash"></i> {{ getStaticTranslation('remove') }}
                            </div>
                        </template>
                </Listbox>
            </Popover>
            <Popover ref="op_actions" :pt="{root: {class: 'border-0'}}">
                <Menu :model="actions">
                    <template #item="slotProps">
                        <a v-ripple class="flex align-items-center" v-bind="slotProps.props.action">
                            <span :class="slotProps.item.icon" />
                            <span class="ml-2">{{ slotProps.item.label }}</span>
                        </a>
                    </template>
                </Menu>
            </Popover>
            <Dialog v-model:visible="deleteTaskDialog" :style="{ width: '450px' }" :header="getStaticTranslation('confirm')" :modal="true">
                <div class="flex items-center gap-4">
                    <i class="pi pi-exclamation-triangle !text-3xl" />
                    <span>{{ getStaticTranslation('confirm_delete_task') }}</span>
                </div>
                <template #footer>
                    <Button :label="getStaticTranslation('no')" icon="pi pi-times" text @click="deleteTaskDialog = false" />
                    <Button :label="getStaticTranslation('yes')" icon="pi pi-check" text @click="deleteTask" />
                </template>
            </Dialog>

            <Popover ref="op_role">
                <div v-if="selectedRole" class="rounded flex flex-col max-w-96">
                    <div class="px-4 py-2">
                        <div>
                            <div class="font-medium mt-1">{{ getDisplayName(selectedRole) }}</div>
                            <div class="text-xs font-light pb-2">{{ getDisplayDescription(selectedRole) }}</div>
                            <div class="text-primary" v-if="selectedRole.users.length">
                                <div v-for="user in selectedRole.users" :key="user.id">
                                    <div>
                                        <span class="pi pi-user mr-2"></span>
                                        {{ user.name }}
                                    </div>
                                </div>
                            </div>

                            <div class="text-yellow-500 flex items-center" v-else><span class="pi pi-ban mr-2"></span> {{ getStaticTranslation('no_one_assigned') }}</div>
                        </div>
                    </div>
                </div>
            </Popover>

            <Dialog v-model:visible="versionDialog" :style="{ width: '450px' }" :header="getStaticTranslation('version')" :modal="true">
                <div>
                    <label for="name" class="block font-bold mb-3">{{ getStaticTranslation('name_number') }}</label>
                    <InputGroup>
                        <InputText id="name" v-model.trim="version.name" required="true" autofocus :invalid="submitted && !version.name" fluid />
                        <InputGroupAddon>.{{ version.number }}</InputGroupAddon>
                        <Button icon="pi pi-save" @click="saveProjectVersion(false, false)" />
                    </InputGroup>
                    <small v-if="submitted && !version.name" class="text-red-500">{{ getStaticTranslation('name_required') }}</small>
                </div>

                <template #footer>
                    <div class="w-full flex justify-between">
                        <Button icon="pi pi-trash" :label="getStaticTranslation('delete')" severity="danger" outlined="" @click="deleteVersionDialog=true" />
                        <Button icon="pi pi-plus" :label="getStaticTranslation('new_version')" outlined @click="addNewVersion" />
                    </div>
                </template>
            </Dialog>

            <Dialog v-model:visible="deleteVersionDialog" :style="{ width: '450px' }" :header="getStaticTranslation('confirm')" :modal="true">
                <div class="flex items-center gap-4">
                    <i class="pi pi-exclamation-triangle !text-3xl" />
                    <span>{{ getStaticTranslation('confirm_delete_version') }} {{version.number}}?</span>
                </div>
                <template #footer>
                    <Button :label="getStaticTranslation('no')" icon="pi pi-times" text @click="deleteVersionDialog = false" />
                    <Button :label="getStaticTranslation('yes')" icon="pi pi-check" text @click="deleteVersion" />
                </template>
            </Dialog>

            <Dialog v-model:visible="sendAgreementDialog" :style="{ width: '450px' }" :header="getStaticTranslation('send_agreements')" :modal="true">
                <div>
                    <div class="mb-5">{{ getStaticTranslation('roles_updated_message') }}</div>
                    <DataTable :value="updatedRoles" scrollable scrollHeight="300px">
                        <Column field="name" :header="getStaticTranslation('role')">
                            <template #body="slotProps">
                                {{ getDisplayName(slotProps.data) }}
                            </template>
                        </Column>
                        <Column :header="getStaticTranslation('users')">
                            <template #body="{data}">
                                <div v-for="user in data.users" :key="user.id">
                                    {{ user.name }}
                                </div>
                            </template>
                        </Column>
                    </DataTable>

                </div>

                <template #footer>
                    <Button :label="getStaticTranslation('cancel')" icon="pi pi-times" text @click="sendAgreementDialog=false" />
                    <Button :label="getStaticTranslation('send')" icon="pi pi-send" @click="sendAgreements" />
                </template>
            </Dialog>

            <Toast />

        </div>
    </div>
</template>

<script>
import mainMixin from '~/mixins/main.mixin.js';
import translationMixin from '~/mixins/translation.mixin.js';
import { FilterService } from '@primevue/core/api';
import { buildRaciExportCsv } from '~/utils/raciCsvExport.js';
import {
    hasVisibleSectionValue as hasVisibleSectionValueUtil,
    planVersionSave,
    rollbackVersionSave,
} from '~/utils/projectVersionSave.js';

// Register custom filter for multilingual JSON fields
FilterService.register('multilingual', (value, filter) => {
    if (filter === undefined || filter === null || filter.trim() === '') {
        return true;
    }

    if (value === undefined || value === null) {
        return false;
    }

    // If value is a JSON object with language keys
    if (typeof value === 'object' && value !== null) {
        const searchTerm = filter.toLowerCase();
        // Search in all language values
        return Object.values(value).some(langValue => {
            if (langValue && typeof langValue === 'string') {
                return langValue.toLowerCase().includes(searchTerm);
            }
            return false;
        });
    }

    // Fallback for string values (backward compatibility)
    if (typeof value === 'string') {
        return value.toLowerCase().includes(filter.toLowerCase());
    }

    return false;
});

// Register unified filter for section columns (Excel-like filtering)
FilterService.register('sectionUnified', (value, filterConfig) => {
    // If no filter config, show all
    if (!filterConfig || !filterConfig.selectedValues || filterConfig.selectedValues.length === 0) {
        return true;
    }

    // Get all language values from multilingual object
    let allLanguageValues = [];

    if (typeof value === 'object' && value !== null) {
        // Extract all language values (NL, FR, EN)
        allLanguageValues = Object.values(value).filter(v => v && typeof v === 'string' && v.trim() !== '');
    } else if (typeof value === 'string' && value.trim() !== '') {
        allLanguageValues = [value];
    }

    // If no values, check if empty string is in selected values
    if (allLanguageValues.length === 0) {
        for (let i = 0; i < filterConfig.selectedValues.length; i++) {
            if (filterConfig.selectedValues[i] === '') return true;
        }
        return false;
    }

    // Check if any language value matches any selected value
    for (let i = 0; i < filterConfig.selectedValues.length; i++) {
        const selectedValue = filterConfig.selectedValues[i];
        for (let j = 0; j < allLanguageValues.length; j++) {
            if (allLanguageValues[j] === selectedValue) {
                return true;
            }
        }
    }

    return false;
});

export default {
    mixins: [mainMixin, translationMixin],
    data() {
        return {
            isLoading: false,
            version: useState('version'),
            versionDialog: false,
            deleteVersionDialog: false,
            sendAgreementDialog: false,
            isSaving: false,
            edited: false,
            selectedRow: null,
            selectedRole: null,
            selectedCodeId: null,
            selectedTasks: null,
            deleteTaskDialog: false,
            tableHeight: 400,
            scrollDown: false,
            timer: null,
            columnSelected: null,
            filters: {
                global: { value: null, matchMode: 'multilingual' },
            },
            debounceTimeout: null,
            page: 0,
            rows: 10,
            // Selector value shown in the rows-per-page dropdown: one of
            // pageSizeOptions' numeric values, or 'all'. Kept distinct from
            // `rows` (the actual numeric value fed to the DataTable/paginator)
            // because "All" has no fixed row count.
            pageSizeOption: 10,
            pageSizeNumericOptions: [10, 20, 50, 100, 200],
            // Sentinel row count used for "All": larger than any realistic
            // project task count, so the paginator renders a single page.
            allRowsValue: 100000,
            selectedSection: null,
            columnFilters: {}, // Store filters for each section column
            hideEmptyValues: false, // Toggle for hiding empty rows and columns
            // Snapshot of a cell's value taken on cell-edit-init, used to
            // restore it on Escape (cell-edit-cancel). The #editor template
            // binds v-model straight to the underlying task object, so
            // without this snapshot Escape would leave partially-typed
            // keystrokes in place instead of reverting them (issue 6).
            cellEditSnapshot: null,
            // Set for the duration of the DOM patch that follows a
            // cell-edit-complete/cancel, so the input's own @blur (fired
            // when the editor unmounts) doesn't run onTaskSectionEdit a
            // second time (double commit) or overwrite a just-reverted value.
            cellEditHandled: false
        };
    },
    methods: {
        async loadVersion() {
            this.isLoading = true;
            this.timer = null;
            this.loadProject();
            if(this.project){
                this.loadPageSizePreference();
                // Initialize filters for each section
                this.project.sections.forEach((_section, index) => {
                    const fieldKey = `sections.${index}.pivot.value`;
                    if (!this.filters[fieldKey]) {
                        this.filters[fieldKey] = { value: null, matchMode: 'sectionUnified' };
                    }
                });

                if(this.project.versions.length == 0){
                    this.version = {id: null, project_id: this.project.id, name: '', number: 1, tasks: []};
                    this.isLoading = false;
                }else{
                    await this.client('/api/project-versions/' + this.project.versions[0].id).then(response => {
                        this.isLoading = false;
                        this.version = response;
                    });
                }
            }
        },
        async loadProject() {
            if(this.project){
                this.project = await this.client('/api/projects/' + this.project.id);
            }
            this.timer = setTimeout(() => this.loadProject(), 1000 * 60 * 5);
        },
        addNewVersion(){
            // Delegates to saveProjectVersion's bumpVersion flag so the id/number
            // bump, the request and the success/error toast all go through the
            // same, single, tested code path (PIPE-565).
            this.saveProjectVersion(true, true);
        },
        deleteVersion(){
            this.deleteVersionDialog = false;
            this.versionDialog = false;
            this.project.versions.shift();
            const number = this.version.number;

            this.client('/api/project-versions/' + this.version.id, {
                method: 'DELETE'
            }).then(_ => this.loadVersion());
            this.$toast.add({ severity: 'success', summary: 'Version deleted', detail: 'The version number ' + number + ' has been deleted.', life: 3000 });

        },
        getRole(taskIndex, role_id) {
             if(this.$refs.roleTable && this.$refs.roleTable.processedData[taskIndex]){
                const role = this.$refs.roleTable.processedData[taskIndex].roles.find(e => e.id == role_id);
                if(role){
                    return role;
                }else{
                    const newRole = JSON.parse(JSON.stringify(this.project.roles.find((e) => e.children.map(f => f.id).includes(role_id)).children.find(g => g.id == role_id)));
                    return { ...newRole,
                        ...{
                            pivot: {
                                task_id: this.$refs.roleTable.processedData[taskIndex].id,
                                role_id: role_id,
                                raci_code_id: null
                            }
                        }
                    };
                }
            }
        },
        getRoleRaciCodeValue(taskIndex, role_id){
            const role = this.getRole((this.page * this.rows) + taskIndex, role_id);
            if (role && role.pivot.raci_code_id) {
                return this.project.raci_codes.find((e) => e.id == role.pivot.raci_code_id);
            }
            return null;
        },
        getTextColorForBackground(hexColor) {
            if (!hexColor) return 'text-gray-900';
            // Remove # if present
            const hex = hexColor.replace('#', '');
            // Convert hex to RGB
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            // Calculate luminance (perceived brightness)
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            // Return white text for dark backgrounds, dark text for light backgrounds
            return luminance < 0.5 ? 'text-white' : 'text-gray-900';
        },
        toggleCodes(event, taskIndex, role) {
            if (this.isProjectAdmin) {
                this.columnSelected = role;
                const index = this.version.tasks.findIndex(e => e.id == this.$refs.roleTable.processedData[(this.page * this.rows) + taskIndex].id);
                this.selectedRow = this.version.tasks[index];
                if(!this.version.tasks[index].roles.find(e => e.id == role.id)){
                    this.version.tasks[index].roles.push(role);
                    this.toggleCodes(event, taskIndex, this.version.tasks[index].roles[this.version.tasks[index].roles.length-1]);
                }else{
                    this.selectedRole = role;
                    this.selectedCodeId = role.pivot.raci_code_id;
                    this.$refs.op.toggle(event);
                }
            }
        },
        setCode(){
            this.selectedRole.pivot.raci_code_id = this.selectedCodeId;
            this.selectedRole.updated = true;
            this.$refs.op.hide();
            this.onEdit();
        },
        toggleActions(event, index) {
            this.selectedRow = this.version.tasks[index];
            this.$refs.op_actions.toggle(event);
        },
        duplicateTask(){
            this.addNew(true, true);
        },
        confirmDeleteTask()
        {
            this.deleteTaskDialog = true;
            this.$refs.op_actions.hide();
        },
        deleteTask(){
            this.version.tasks = this.version.tasks.filter(val => this.selectedRow.id != val.id);
            this.selectedRow = null;
            this.deleteTaskDialog = false;
            this.onEdit();
        },
        addNew(copy = false, fromRow = false) {
            this.page = Math.floor(this.version.tasks.length / this.rows);
            this.$nextTick(() => {
                if (this.$refs.roleTable) {
                    // Setting d_first is enough: PrimeVue's DataTable paginator is
                    // bound reactively to this internal state (:first="d_first"),
                    // it re-renders on its own. DataTable does not expose an
                    // "updatePaginatorState" method, calling it threw a TypeError
                    // on every save (see PIPE-565 issue 3).
                    this.$refs.roleTable.d_first = this.page * this.rows;
                }
            });
            let newTask;
            if(!fromRow){
                this.selectedRow = false;
            }
            if(this.selectedRow && copy){
                newTask = JSON.parse(JSON.stringify(this.selectedRow));
                newTask.id = null;
                newTask.position = newTask.position + 1;
            }else{
                newTask = {
                    position: this.selectedRow ? (this.selectedRow.position+1) : this.version.tasks.length,
                    sections: this.project.sections.map(section => ({
                        ...section,
                        pivot: {
                            value: { NL: '' }
                        }
                    })),
                    roles: [],
                }
                this.scrollDown = true;

            }
            this.version.tasks.splice(newTask.position, 0, newTask);
            // Update positions only for tasks after the inserted one
            for(let i = newTask.position + 1; i < this.version.tasks.length; i++) {
                this.version.tasks[i].position = i;
            }
            this.selectedRow = null;
            this.$refs.op_actions.hide();
            this.onEdit();
        },
        onRowReorder(event) {
            if(event){
                this.version.tasks = event.value;
            }
            this.version.tasks.forEach((task, index) => {
                task.position = index
            })
            this.onEdit();
        },
        saveProjectVersion(loadProject=false, bumpVersion=false){
            this.versionDialog = false;
            this.isSaving = true;
            this.flushSave();

            setTimeout(async () => {
                // Snapshot for rollback taken right before mutating, so it
                // reflects section-value cleanup too (see planVersionSave in
                // utils/projectVersionSave.js).
                let rollback = null;

                try {
                    if (this.project) {
                        this.project.action = 'tasks';
                    }

                    // Clean up empty task section values before saving
                    if (this.version && this.version.tasks) {
                        this.version.tasks.forEach(task => {
                            if (task.sections) {
                                task.sections.forEach(section => {
                                    if (section.pivot && section.pivot.value) {
                                        // Ensure value is properly formatted
                                        if (typeof section.pivot.value === 'object') {
                                            // Remove empty language entries
                                            Object.keys(section.pivot.value).forEach(key => {
                                                if (!section.pivot.value[key] || section.pivot.value[key].trim() === '') {
                                                    delete section.pivot.value[key];
                                                }
                                            });

                                            // If no language entries remain, set to null
                                            if (Object.keys(section.pivot.value).length === 0) {
                                                section.pivot.value = null;
                                            }
                                        } else if (!section.pivot.value || section.pivot.value.trim() === '') {
                                            section.pivot.value = null;
                                        }
                                    }
                                });
                            }
                        });
                    }

                    // A deliberate save (the "Opslaan" button) always creates the
                    // next version: null the version id and every task id so the
                    // request POSTs a brand new version row, and bump the number
                    // so the tag at the bottom of the matrix advances (PIPE-565
                    // issue 2). Auto-save (debounce/beforeUnmount) keeps updating
                    // the current version in place instead of creating a version
                    // on every idle tick. See utils/projectVersionSave.js.
                    const planned = planVersionSave(this.version, { bumpVersion });
                    this.version = planned.version;
                    rollback = planned.rollback;

                    if (this.version && this.version.id) {
                        this.version = await this.client('/api/project-versions/' + this.version.id, {
                            method: "PUT",
                            body: this.version
                        });
                    } else {
                        this.version = await this.client('/api/project-versions', {
                            method: "POST",
                            body: this.version
                        });
                    }
                    if (loadProject) {
                        this.project = await this.client('/api/projects/' + this.project.id);
                    }

                    this.edited = false;

                    if (bumpVersion) {
                        this.$toast.add({
                            severity: 'success',
                            summary: this.getStaticTranslation('new_version'),
                            detail: 'The version number ' + this.version.number + ' has been created.',
                            life: 3000
                        });
                    }

                    if(this.scrollDown){
                        const roleTableElement = document.querySelector('.roleTable');
                        if (roleTableElement) {
                            const parentElement = roleTableElement.parentElement;
                            if (parentElement) {
                                parentElement.scrollTop = parentElement.scrollHeight;
                            }
                        }
                        this.scrollDown = false;
                    }
                } catch (error) {
                    // Surface the failure: previously a failed save left the UI
                    // looking unchanged, with no toast/error and the spinner
                    // stuck forever (PIPE-565 issue 5). The unsaved edits stay
                    // in this.version.tasks (we never overwrite it on failure),
                    // so nothing is lost and the user can retry.
                    if (bumpVersion) {
                        // Roll back the optimistic id/number bump: the tag must
                        // not silently "advance" on screen when nothing was
                        // actually created server-side.
                        rollbackVersionSave(this.version, rollback);
                    }
                    console.error('Failed to save project version', error);
                    this.$toast.add({
                        severity: 'error',
                        summary: this.getStaticTranslation('save'),
                        detail: this.getStaticTranslation('save_failed'),
                        life: 5000
                    });
                } finally {
                    this.isSaving = false;
                }
            }, 100);
        },
        selectRole(role) {
            if (this.user) {
                this.$router.push('/roles/' + this.project.id + '-' + role.parent_id + '-' + role.id);
            }
        },
        displayRole(event, role) {
            if (this.selectedRole === role) {
                this.$refs.op_role.hide();
                this.selectedRole = null;
            } else {
                this.hidePopover();
                this.selectedRole= role;
                this.$refs.op_role.show(event);
            }
        },
        hidePopover() {
            this.$refs.op_role.hide();
        },
        onResize(){
            this.tableHeight = (window.innerHeight - 105);
        },
        exportCSV(){
            // CSV building itself lives in utils/raciCsvExport.js (pure, unit-tested):
            // it keys every column on section/role names instead of position, only
            // emits a category header cell for roles that actually have children, and
            // CSV-escapes values so a semicolon/quote/newline in a cell can't shift
            // columns on re-import (bug report item 8).
            const csv = buildRaciExportCsv({
                sections: this.project.sections,
                roles: this.project.roles,
                tasks: this.version.tasks,
                raciCodes: this.project.raci_codes,
                currentViewLanguage: this.currentViewLanguage,
            });

            // A Blob (rather than a data: URI + encodeURI) preserves the UTF-8 BOM
            // byte-for-byte and isn't subject to URL length limits on large exports.
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const csv_link = document.createElement('a');
            csv_link.href = url;
            csv_link.target = '_blank';
            csv_link.download = 'raci_' + this.project.name + '.csv';
            csv_link.click();
            URL.revokeObjectURL(url);
        },
        sendAgreements(){
            let agreement = {
                id: null,
                role_id: null,
                old_user_id: null,
                new_user_id: null,
                active: false,
            };

            for(const role of this.updatedRoles){
                agreement.role_id = role.id;
                role.users.forEach(user => {
                    agreement.old_user_id = user.id;
                    agreement.new_user_id = user.id;
                    this.project.roles.find((e) => e.children.map(f => f.id).includes(role.id)).children.find(g => g.id == role.id).agreements.unshift(agreement);
                })
            }
            this.saveProject();
            this.sendAgreementDialog = false;
        },
        async saveProject(){
            this.project.action = 'roles';
            this.project = await this.client('/api/projects/' + this.project.id, {
                method: "PUT",
                body: this.project
            });
            this.loadVersion();
        },
        onEdit() {
            this.edited = true;
            this.debouncedSave();
        },
        flushSave() {
            clearTimeout(this.debounceTimeout);
        },
        debouncedSave() {
            if (this.debounceTimeout) this.flushSave();
            this.debounceTimeout = setTimeout(() => {
                this.saveProjectVersion();
            }, 10000);
        },
        onPageChange(event) {
            this.page = event.page;
            this.rows = event.rows;
        },
        // Rows-per-page persistence (issue 7): stored per project, like the
        // hidden-columns preference below, guarded with try/catch since
        // localStorage can throw (private mode, quota, disabled storage).
        getPageSizeStorageKey() {
            return `raci_page_size_project_${this.project.id}`;
        },
        loadPageSizePreference() {
            let stored = null;
            try {
                stored = localStorage.getItem(this.getPageSizeStorageKey());
            } catch (e) {
                console.error('Failed to read rows-per-page preference', e);
            }

            if (stored === 'all') {
                this.applyPageSize('all');
                return;
            }

            const parsed = parseInt(stored, 10);
            if (this.pageSizeNumericOptions.includes(parsed)) {
                this.applyPageSize(parsed);
                return;
            }

            this.applyPageSize(10);
        },
        savePageSizePreference(value) {
            try {
                localStorage.setItem(this.getPageSizeStorageKey(), String(value));
            } catch (e) {
                console.error('Failed to persist rows-per-page preference', e);
            }
        },
        applyPageSize(value) {
            this.pageSizeOption = value;
            this.rows = value === 'all' ? this.allRowsValue : value;
        },
        onPageSizeChange(value) {
            this.applyPageSize(value);
            this.page = 0;
            this.$nextTick(() => {
                // Same reasoning as in addNew(): d_first is the DataTable's
                // own reactive paginator state, there is no public method to
                // force a re-render of it (PIPE-565 issue 3).
                if (this.$refs.roleTable) {
                    this.$refs.roleTable.d_first = 0;
                }
            });
            this.savePageSizePreference(value);
        },
        // Cell edit commit/cancel (issue 6): the #editor template's v-model
        // writes straight into the task object, so we snapshot the value on
        // cell-edit-init and restore it on cell-edit-cancel (Escape). Enter
        // (and any other completion, e.g. Tab/blur/outside click) commits by
        // running the existing autosave trigger.
        getEditedSectionIndex(field) {
            const match = /^sections\.(\d+)\.pivot\.value$/.exec(field || '');
            return match ? parseInt(match[1], 10) : null;
        },
        onCellEditInit(event) {
            const sectionIndex = this.getEditedSectionIndex(event.field);
            if (sectionIndex === null) return;
            const pivot = event.data.sections?.[sectionIndex]?.pivot;
            if (!pivot) return;
            this.cellEditSnapshot = {
                taskId: event.data.id,
                sectionIndex,
                value: JSON.parse(JSON.stringify(pivot.value ?? null))
            };
        },
        onCellEditCancel(event) {
            const sectionIndex = this.getEditedSectionIndex(event.field);
            if (sectionIndex === null || !this.cellEditSnapshot) return;
            if (this.cellEditSnapshot.taskId !== event.data.id || this.cellEditSnapshot.sectionIndex !== sectionIndex) return;

            const pivot = event.data.sections?.[sectionIndex]?.pivot;
            if (pivot) {
                pivot.value = JSON.parse(JSON.stringify(this.cellEditSnapshot.value));
            }
            this.cellEditSnapshot = null;
            this.markCellEditHandled();
        },
        onCellEditComplete(event) {
            const sectionIndex = this.getEditedSectionIndex(event.field);
            if (sectionIndex === null) return;
            this.cellEditSnapshot = null;
            const pivot = event.data.sections?.[sectionIndex]?.pivot;
            if (pivot) {
                this.onTaskSectionEdit(pivot);
            }
            this.markCellEditHandled();
        },
        markCellEditHandled() {
            this.cellEditHandled = true;
            this.$nextTick(() => {
                this.cellEditHandled = false;
            });
        },
        getIndex(props) {
            return (this.page * this.rows) + props.index;
        },
        toggleColumnVisibility(sectionId) {
            const section = this.project.sections.find(s => s.id === sectionId);
            if (section) {
                section.show_in_table = !section.show_in_table;

                // Save to localStorage for temporary hiding
                const hiddenColumns = this.getHiddenColumnsFromStorage();
                if (section.show_in_table) {
                    // Remove from hidden list
                    const index = hiddenColumns.indexOf(sectionId);
                    if (index > -1) {
                        hiddenColumns.splice(index, 1);
                    }
                } else {
                    // Add to hidden list
                    if (!hiddenColumns.includes(sectionId)) {
                        hiddenColumns.push(sectionId);
                    }
                }
                localStorage.setItem(`hidden_columns_project_${this.project.id}`, JSON.stringify(hiddenColumns));
            }
        },
        getHiddenColumnsFromStorage() {
            const stored = localStorage.getItem(`hidden_columns_project_${this.project.id}`);
            return stored ? JSON.parse(stored) : [];
        },
        showHiddenColumnsMenu(event) {
            this.$refs.hiddenColumnsPopover.toggle(event);
        },
        onSectionChange() {
            if (this.selectedSection) {
                // Update globalFilterFields to only search in the selected section
                const sectionIndex = this.project.sections.findIndex(s => s.id === this.selectedSection);
                this.$refs.roleTable.globalFilterFields = [`sections.${sectionIndex}.pivot.value`];
            } else {
                // Reset to search in all sections
                this.$refs.roleTable.globalFilterFields = this.project.sections.map((section, index) => 'sections.' + index + '.pivot.value');
            }
        },
        // Display methods
        getDisplayName(item) {
            // If name is a JSON object, get the value for current language
            if (typeof item.name === 'object' && item.name !== null) {
                return item.name[this.currentViewLanguage] || item.name['NL'] || '';
            }
            // If name is a string, return it directly (for backward compatibility)
            return item.name || '';
        },
        getDisplayDescription(item) {
            // If description is a JSON object, get the value for current language
            if (typeof item.description === 'object' && item.description !== null) {
                return item.description[this.currentViewLanguage] || item.description['NL'] || '';
            }
            // If description is a string, return it directly (for backward compatibility)
            return item.description || '';
        },
        getDisplaySectionName(section) {
            // If name is a JSON object, get the value for current language
            if (typeof section.name === 'object' && section.name !== null) {
                return section.name[this.currentViewLanguage] || section.name['NL'] || '';
            }
            // If name is a string, return it directly (for backward compatibility)
            return section.name || '';
        },
        getDisplayRaciCodeName(raciCode) {
            if (!raciCode) return '';
            // If name is a JSON object, get the value for current language
            if (typeof raciCode.name === 'object' && raciCode.name !== null) {
                return raciCode.name[this.currentViewLanguage] || raciCode.name['NL'] || '';
            }
            // If name is a string, return it directly (for backward compatibility)
            return raciCode.name || '';
        },
        getDisplayTaskSectionValue(value) {
            if (!value) return '';

            // If value is a JSON string, try to parse it
            if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
                try {
                    const parsed = JSON.parse(value);
                    if (typeof parsed === 'object' && parsed !== null) {
                        return parsed[this.currentViewLanguage] || parsed['NL'] || '';
                    }
                } catch (e) {
                    // If parsing fails, treat as regular string
                    return value;
                }
            }

            // If value is a JSON object, get the value for current language
            if (typeof value === 'object' && value !== null) {
                return value[this.currentViewLanguage] || value['NL'] || '';
            }

            // If value is a string, return it directly (for backward compatibility)
            return String(value);
        },
        // Thin wrapper so the #body template (Options API, no <script setup>)
        // can call the pure, unit-tested utility (PIPE-565 issue 4).
        hasVisibleSectionValue(task, sectionIndex) {
            return hasVisibleSectionValueUtil(task, sectionIndex, this.currentViewLanguage);
        },
        onSectionInputBlur(taskSectionPivot) {
            // Skip the commit triggered by the editor unmounting right after
            // Enter/Tab/Escape already handled it via cell-edit-complete/
            // cell-edit-cancel, to avoid a double commit or overwriting a
            // just-reverted value with a fresh "edited" autosave cycle.
            if (this.cellEditHandled) {
                this.cellEditHandled = false;
                return;
            }
            this.onTaskSectionEdit(taskSectionPivot);
        },
        async onTaskSectionEdit(taskSectionPivot) {
            // Ensure value is an object with all languages initialized
            if (typeof taskSectionPivot.value !== 'object' || taskSectionPivot.value === null) {
                taskSectionPivot.value = { NL: '', FR: '', EN: '' };
            }

            // Ensure all language keys exist
            if (!taskSectionPivot.value.NL) taskSectionPivot.value.NL = '';
            if (!taskSectionPivot.value.FR) taskSectionPivot.value.FR = '';
            if (!taskSectionPivot.value.EN) taskSectionPivot.value.EN = '';

            // Check if there's actual content in any language
            const hasContent = Object.values(taskSectionPivot.value).some(v => v && v.trim() !== '');
            if (hasContent) {
                // Auto-translate if needed
                const { autoTranslateTaskSections } = useTaskSectionTranslation();
                try {
                    await autoTranslateTaskSections([{ value: taskSectionPivot.value }]);
                } catch (error) {
                    console.error('Auto-translation failed:', error);
                }
            }

            this.onEdit();
        },
        // Column filter methods
        getUniqueValuesForSection(sectionIndex, excludeCurrentSection = false) {
            if (!this.version || !this.version.tasks) return [];

            // Get filtered tasks based on OTHER active filters (not the current section)
            let tasksToConsider = this.version.tasks;

            if (excludeCurrentSection) {
                // Apply all filters except the current section's filter
                tasksToConsider = this.version.tasks.filter(task => {
                    return this.project.sections.every((_section, idx) => {
                        // Skip the current section we're filtering
                        if (idx === sectionIndex) return true;

                        const fieldKey = `sections.${idx}.pivot.value`;
                        const filterValue = this.filters[fieldKey]?.value;

                        // If no filter for this section, include the task
                        if (!filterValue) return true;

                        const taskValue = task.sections?.[idx]?.pivot?.value;

                        // Apply the filter logic manually (only object filters now)
                        if (typeof filterValue === 'object' && filterValue !== null && filterValue.selectedValues && filterValue.selectedValues.length > 0) {
                            // Get all language values
                            let allLanguageValues = [];

                            if (typeof taskValue === 'object' && taskValue !== null) {
                                allLanguageValues = Object.values(taskValue).filter(v => v && typeof v === 'string' && v.trim() !== '');
                            } else if (typeof taskValue === 'string' && taskValue.trim() !== '') {
                                allLanguageValues = [taskValue];
                            }

                            // If no values, check if empty string is in selected values
                            if (allLanguageValues.length === 0) {
                                return filterValue.selectedValues.includes('');
                            }

                            // Check if any language value matches any selected value
                            let foundMatch = false;
                            for (let i = 0; i < filterValue.selectedValues.length; i++) {
                                for (let j = 0; j < allLanguageValues.length; j++) {
                                    if (allLanguageValues[j] === filterValue.selectedValues[i]) {
                                        foundMatch = true;
                                        break;
                                    }
                                }
                                if (foundMatch) break;
                            }

                            return foundMatch;
                        }

                        return true;
                    });
                });
            }

            const valuesSet = new Set();
            tasksToConsider.forEach(task => {
                if (task.sections && task.sections[sectionIndex] && task.sections[sectionIndex].pivot) {
                    const value = task.sections[sectionIndex].pivot.value;
                    const displayValue = this.getDisplayTaskSectionValue(value);
                    if (displayValue && displayValue.trim() !== '') {
                        valuesSet.add(displayValue);
                    }
                }
            });

            return Array.from(valuesSet).sort();
        },
        getAvailableValuesWithStatus(sectionIndex) {
            // Get all possible values for this section
            const allValues = this.getUniqueValuesForSection(sectionIndex, false);
            // Get values that would be available if other filters are applied
            const availableValues = this.getUniqueValuesForSection(sectionIndex, true);

            return allValues.map(value => ({
                value,
                disabled: !availableValues.includes(value)
            }));
        },
        toggleColumnFilter(event, sectionIndex) {
            const filterKey = `section_${sectionIndex}`;
            if (!this.columnFilters[filterKey]) {
                this.columnFilters[filterKey] = {
                    selectedValues: [],
                    availableValues: this.getAvailableValuesWithStatus(sectionIndex)
                };
            } else {
                // Refresh available values with current filter state
                this.columnFilters[filterKey].availableValues = this.getAvailableValuesWithStatus(sectionIndex);
            }

            // Toggle the filter popover
            const popoverRef = this.$refs[`filterPopover_${sectionIndex}`];
            if (popoverRef && popoverRef[0]) {
                popoverRef[0].toggle(event);
            }
        },
        applyColumnFilter(sectionIndex) {
            const filterKey = `section_${sectionIndex}`;
            const filter = this.columnFilters[filterKey];

            if (!filter) return;

            const fieldKey = `sections.${sectionIndex}.pivot.value`;

            if (filter.selectedValues.length > 0) {
                // Convert Vue Proxy array to plain array for proper comparison
                const plainArray = [...filter.selectedValues];

                this.filters[fieldKey] = {
                    value: {
                        selectedValues: plainArray
                    },
                    matchMode: 'sectionUnified'
                };
            } else {
                // No filter active, reset
                this.filters[fieldKey] = {
                    value: null,
                    matchMode: 'sectionUnified'
                };
            }

            // Force trigger filter update by reassigning the whole filters object
            this.filters = { ...this.filters };

            // Close the popover
            const popoverRef = this.$refs[`filterPopover_${sectionIndex}`];
            if (popoverRef && popoverRef[0]) {
                popoverRef[0].hide();
            }
        },
        clearColumnFilter(sectionIndex) {
            const filterKey = `section_${sectionIndex}`;
            delete this.columnFilters[filterKey];

            const fieldKey = `sections.${sectionIndex}.pivot.value`;

            // Reset filter
            this.filters[fieldKey] = {
                value: null,
                matchMode: 'sectionUnified'
            };

            // Force trigger filter update by reassigning the whole filters object
            this.filters = { ...this.filters };

            // Close the popover
            const popoverRef = this.$refs[`filterPopover_${sectionIndex}`];
            if (popoverRef && popoverRef[0]) {
                popoverRef[0].hide();
            }
        },
        hasActiveColumnFilter(sectionIndex) {
            const filterKey = `section_${sectionIndex}`;
            const filter = this.columnFilters[filterKey];
            return filter && filter.selectedValues.length > 0;
        },
        toggleHideEmptyValues() {
            this.hideEmptyValues = !this.hideEmptyValues;
        },
        getFilteredTasksBySection() {
            if (!this.version || !this.version.tasks) return [];

            // Apply all section filters to tasks
            return this.version.tasks.filter(task => {
                return this.project.sections.every((_section, idx) => {
                    const fieldKey = `sections.${idx}.pivot.value`;
                    const filterValue = this.filters[fieldKey]?.value;

                    // If no filter for this section, include the task
                    if (!filterValue || !filterValue.selectedValues || filterValue.selectedValues.length === 0) {
                        return true;
                    }

                    const taskValue = task.sections?.[idx]?.pivot?.value;

                    // Get all language values
                    let allLanguageValues = [];

                    if (typeof taskValue === 'object' && taskValue !== null) {
                        allLanguageValues = Object.values(taskValue).filter(v => v && typeof v === 'string' && v.trim() !== '');
                    } else if (typeof taskValue === 'string' && taskValue.trim() !== '') {
                        allLanguageValues = [taskValue];
                    }

                    // If no values, check if empty string is in selected values
                    if (allLanguageValues.length === 0) {
                        return filterValue.selectedValues.includes('');
                    }

                    // Check if any language value matches any selected value
                    for (let i = 0; i < filterValue.selectedValues.length; i++) {
                        for (let j = 0; j < allLanguageValues.length; j++) {
                            if (allLanguageValues[j] === filterValue.selectedValues[i]) {
                                return true;
                            }
                        }
                    }

                    return false;
                });
            });
        },
        taskHasRaciCode(task) {
            if (!task || !this.project || !this.project.roles) return false;

            // Check if task has at least one RACI code assigned
            for (const role of this.project.roles) {
                if (role.children) {
                    for (const child of role.children) {
                        const roleValue = task.roles?.find(r => r.id === child.id);
                        if (roleValue && roleValue.pivot && roleValue.pivot.raci_code_id) {
                            return true;
                        }
                    }
                }
            }
            return false;
        },
        roleHasRaciCode(role) {
            if (!this.version || !this.version.tasks || !role) return false;

            // Get tasks filtered by section filters
            let tasksToCheck = this.getFilteredTasksBySection();

            // If hideEmptyValues is active, also filter out tasks without RACI codes
            if (this.hideEmptyValues) {
                tasksToCheck = tasksToCheck.filter(task => this.taskHasRaciCode(task));
            }

            // Check if role has at least one RACI code assigned in any visible task
            for (const task of tasksToCheck) {
                const roleValue = task.roles?.find(r => r.id === role.id);
                if (roleValue && roleValue.pivot && roleValue.pivot.raci_code_id) {
                    return true;
                }
            }
            return false;
        }
    },
    computed:{
        actions() {
            return [
                {
                    label: this.getStaticTranslation('duplicate'),
                    icon: 'pi pi-copy',
                    command: () => {
                        this.duplicateTask();
                    }
                },
                {
                    label: this.getStaticTranslation('create_new_task_below'),
                    icon: 'pi pi-plus',
                    command: () => {
                        this.addNew(false,true);
                    }
                },
                {
                    label: this.getStaticTranslation('delete'),
                    icon: 'pi pi-trash',
                    command: () => {
                        this.confirmDeleteTask();
                    }
                },
            ];
        },
        hiddenColumnsCount() {
            if (!this.project || !this.project.sections) return 0;
            return this.project.sections.filter(section => !section.show_in_table).length;
        },
        hiddenSections() {
            if (!this.project || !this.project.sections) return [];
            return this.project.sections.filter(section => !section.show_in_table);
        },
        sectionOptions() {
            if (!this.project || !this.project.sections) return [];
            return this.project.sections.filter(section => section.show_in_table).map(section => ({
                id: section.id,
                name: this.getDisplaySectionName(section)
            }));
        },
        filteredTasks() {
            if (!this.version || !this.version.tasks) return [];
            if (!this.hideEmptyValues) return this.version.tasks;

            return this.version.tasks.filter(task => this.taskHasRaciCode(task));
        },
        visibleRoles() {
            if (!this.project || !this.project.roles) return [];
            if (!this.hideEmptyValues) return this.project.roles;

            // Filter out roles whose children are all empty
            return this.project.roles.map(role => {
                if (!role.children) return role;

                const visibleChildren = role.children.filter(child => this.roleHasRaciCode(child));

                return {
                    ...role,
                    children: visibleChildren,
                    _allChildrenHidden: visibleChildren.length === 0
                };
            }).filter(role => !role._allChildrenHidden);
        },
        raciCodes(){
            const codes = [ ...this.project.raci_codes, {
                    id: 0,
                    code: '- empty -',
                    color: '',
                    icon: 'pi pi-plus',
                }];
            return codes;
        },
        updatedRoles(){
            const roles = this.project.roles.map(role => role.children.map((child) => child)).flat();
            let rolesupdated = [];
            if(roles.length && this.version){
                for(const role of roles){
                    for(const task of this.version.tasks){
                        const taskRole = task.roles.find(e => e.id == role.id);
                        if(taskRole && role.agreements.length && taskRole.pivot.raci_code_id && this.$dayjs(taskRole.pivot.updated_at) > this.$dayjs(role.agreements[0].created_at)){
                            if(!rolesupdated.map(e => e.id).includes(role.id)){
                                rolesupdated.push(role);
                            }
                        }
                    }
                }
            }
            return rolesupdated;
        },
        getRoles() {
            return this.visibleRoles.map(role => role.children.map((child) => child)).flat();
        },
        pageSizeOptions() {
            return [
                ...this.pageSizeNumericOptions.map(value => ({ label: String(value), value })),
                { label: this.getStaticTranslation('all'), value: 'all' }
            ];
        },
    },
    watch: {
        project(newValue, oldValue) {
            if (!oldValue || (oldValue.id != newValue.id)) {
                this.loadVersion();
            }
        }
    },
    mounted() {
        this.loadVersion();
        this.$nextTick(function () {
            this.onResize();
            // Initialize filters for each section
            if (this.project && this.project.sections) {
                this.project.sections.forEach((_section, index) => {
                    const fieldKey = `sections.${index}.pivot.value`;
                    if (!this.filters[fieldKey]) {
                        this.filters[fieldKey] = { value: null, matchMode: 'sectionUnified' };
                    }
                });
            }
        })
        window.addEventListener('resize', this.onResize);

        // Initialize with user's language if available

    },
    beforeUnmount() {
        this.saveProjectVersion();
        window.removeEventListener("beforeunload", this.saveProjectVersion);
    },
};
</script>

<style scoped>
.p-datatable-column-title {
   font-weight: 200 !important;
}
</style>
